import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { Avatar } from "@whoosh/design-system/src/components/display";
import {
  Text,
  Box,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { getBookingPlayerNoun } from "../../../../utils/getBookingPlayerNoun";
import { useGetLineItemsAndPriceForRound } from "../../hooks/useGetLineItemsAndPriceForRound";
import { RoundDetailsAnimationWrapper } from "./RoundDetailsAnimationWrapper";
import { ConsolidatedNewPlayers_Round$key } from "./__generated__/ConsolidatedNewPlayers_Round.graphql";

type Props = {
  count: number;
  roundRef: ConsolidatedNewPlayers_Round$key | null;
  roundId: string;
  viewMore: boolean;
};

export const ConsolidatedNewPlayers = ({
  count,
  roundRef,
  roundId,
  viewMore,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const roundData = useFragment(ConsolidatedNewPlayersFragmentString, roundRef);
  const playerNoun = getBookingPlayerNoun({
    bookingType: roundData?.booking?.__typename,
    copyOverrides:
      roundData?.booking?.startSlot?.agenda?.facility?.copyOverrides,
  });
  const { lineItemsForRound } = useGetLineItemsAndPriceForRound({
    fragmentRef: roundData ?? null,
    roundId: roundId,
  });

  if (count <= 0) return null;

  return (
    <Box gap={1} paddingVertical={2}>
      <HStack gap={3} alignItems="center">
        <Avatar size="xs" type="unknownCustomer" />
        <Text variant="mainBaseSemiBold">
          {`${count} ${t`New`} ${playerNoun.plural}`}
        </Text>
      </HStack>
      <RoundDetailsAnimationWrapper enterFrom="bottom" hide={!viewMore}>
        <Box>
          {lineItemsForRound?.map((lineItem, idx) => (
            <Box key={idx}>
              <HStack
                justifyContent="space-between"
                style={{ marginLeft: 36 }}
                alignItems="center"
              >
                <Text variant="mainBaseMedium" color="text-primary">
                  {lineItem?.name}
                </Text>
                <Text variant="mainBaseMedium" color="text-primary">
                  {`${currencySymbol}${ConvertCentsToDollars(
                    lineItem?.price ?? 0
                  )}`}
                </Text>
              </HStack>
            </Box>
          ))}
        </Box>
      </RoundDetailsAnimationWrapper>
    </Box>
  );
};

const ConsolidatedNewPlayersFragmentString = graphql`
  fragment ConsolidatedNewPlayers_Round on Round {
    id
    booking {
      __typename
      startSlot {
        agenda {
          facility {
            copyOverrides {
              lessonPlayer {
                plural
              }
              bookingPlayer {
                plural
              }
            }
          }
        }
      }
    }
    ...useGetLineItemsAndPriceForRound_Round
  }
`;
